import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Tipos para las rutas de navegación
interface NavItem {
  href: string;
  label: string;
  target?: string;
}

// Configuración de navegación
const NAV_ITEMS: NavItem[] = [
  {
    href: "/",
    label: "Inicio",
  },
  {
    href: "/agenda",
    label: "Agenda",
  },
  {
    href: "/informacion-practica",
    label: "Información práctica",
  },
  {
    href: "https://charlas-open-space.jakala.es/",
    label: "Propuesta de charlas",
    target: "_blank",
  },
  {
    href: "/codigo-conducta",
    label: "Código de conducta",
  },
];

// Componente para el logo grande
const LogoLarge: React.FC = () => (
        <a
          href="/"
          className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
    <img
      src="/images/Logo.svg"
      alt="Jakala Open Space, ir a inicio"
      className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto"
    />
  </a>
);

// Componente para los elementos de navegación
const NavigationItem: React.FC<{
  item: NavItem;
  isActive: boolean;
  onClick?: () => void;
}> = ({ item, isActive, onClick }) => {
  const baseClasses =
    "text-white hover:text-jakala-red transition-all duration-300 font-medium text-base block py-3 px-4 rounded-lg hover:bg-white/10 hover:translate-x-2";
  const activeClasses = isActive
    ? "bg-jakala-red/20 border-l-4 border-jakala-red"
    : "";

  return (
    <li>
      <a
        href={item.href}
        className={`${baseClasses} ${activeClasses} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white`}
        target={item.target}
        rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        aria-current={isActive ? "page" : undefined}
        onClick={onClick}
      >
        {item.label}
        {item.target === "_blank" && (
          <span className="sr-only"> (se abre en una pestaña nueva)</span>
        )}
      </a>
    </li>
  );
};

// Componente principal del Header
const Header: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen]);

  return (
    <header className="text-white mx-auto max-w-[1440px] 3xl:max-w-[1920px] py-4 absolute top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-14 flex justify-between items-center lg:justify-center">
        {/* Logo */}
        <LogoLarge />

        {/* Botón Hamburguesa */}
        <button
          type="button"
          onClick={toggleMenu}
          className="lg:absolute lg:right-4 lg:top-1/2 lg:transform lg:-translate-y-1/2 z-[60] relative p-2 hover:bg-white/10 rounded-lg transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={isMenuOpen}
          aria-controls="menu-principal"
        >
          <div className="w-6 h-5 flex flex-col justify-between" aria-hidden="true">
            <span
              className={`block h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-[10px]" : ""
              }`}
            />
            <span
              className={`block h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-[10px]" : ""
              }`}
            />
          </div>
        </button>

        {/* Overlay para cerrar el menú - Detrás del menú */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={closeMenu}
            aria-hidden="true"
          />
        )}

        {/* Menú desplegable - Encima del overlay */}
        <nav
          id="menu-principal"
          aria-label="Menú principal"
          aria-hidden={!isMenuOpen}
          inert={!isMenuOpen ? true : undefined}
          className={`fixed top-0 right-0 h-screen w-64 bg-Colors-blue-dark shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          } lg:w-72`}
        >
          <div className="pt-24 px-6">
            <ul className="flex flex-col gap-2">
              {NAV_ITEMS.map((item) => (
                <NavigationItem
                  key={item.href}
                  item={item}
                  isActive={location.pathname === item.href}
                  onClick={closeMenu}
                />
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
